import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'
import { Observable } from 'rxjs'
import {
  SearchResult,
  BuildSearchParams,
} from '@/app/core/service/table.service'
import { environment } from '@environment/environment'
import {
  TaxonTableQuery, TaxonPreenchimentoFiltroCadeiaPayload, TaxonPreenchimentoFiltroCadeiaResponse, TaxonBuscaRapidaDto,
} from '../models/taxon.model'
import { TaxonDetailsDto } from '../models/taxon-details.model'
import { TaxonAtualizarDto } from '../models/taxon-atualizar.model'
import { TaxonRegistrarDto } from '../models/taxon-registrar.model'
import { TaxonClasseTaxonomicaAtualizarDto } from '../models/taxon-classe-taxonomica-atualizar.model'
import { TaxonAmeacasAtualizarDto } from '../models/taxon-ameacas-atualizar.model'
import { TaxonPopulacaoAtualizarDto } from '../models/taxon-populacao-atualizar.model'
import { TaxonDadosEcologicosAtualizarDto } from '../models/taxon-dados-ecologicos-atualizar.model'
import { TaxonDadosDistribuicaoAtualizarDto } from '../models/taxon-dados-distribuicao-atualizar.model'
import { TaxonUsosAtualizarDto } from '../models/taxon-usos-atualizar.model'
import { TaxonConservacaoAtualizarDto } from '../models/taxon-conservacao-atualizar.model'
import { TaxonPesquisaAtualizarDto } from '../models/taxon-pesquisa-atualizar.model'
import { TaxonMultimidiaAtualizarDto } from '../models/taxon-multimidia-atualizar.model'
import { TaxonValidacaoAtualizarDto } from '../models/taxon-validacao-atualizar.model'

@Injectable({
  providedIn: 'root',
})
export class TaxonService {
  private readonly apiUrl = `${environment.basePath}/taxons`
  private readonly http = inject(HttpClient)

  listarPaginado(query: TaxonTableQuery): Observable<SearchResult<TaxonDetailsDto>> {
    let params = new HttpParams()
      .set('page', query.page)
      .set('pageSize', query.pageSize)
      .set('sortColumn', String(query.sortColumn ?? ''))
      .set('sortDirection', query.sortDirection ?? '')

    params = BuildSearchParams(params, query, 'termoBusca')

    return this.http.get<SearchResult<TaxonDetailsDto>>(this.apiUrl, { params })
  }

  buscarRapido(termo: string, grupoListaId: number): Observable<TaxonBuscaRapidaDto[]> {
    const params = new HttpParams().set('termo', termo).set('grupoListaId', grupoListaId)
    return this.http.get<TaxonBuscaRapidaDto[]>(`${this.apiUrl}/busca`, { params })
  }

  preenchimentoFiltroCadeia(payload: TaxonPreenchimentoFiltroCadeiaPayload): Observable<TaxonPreenchimentoFiltroCadeiaResponse> {
    let params = new HttpParams()
      .set('returnCampo', payload.returnCampo)
      .set('sortColumn', String(payload.sortColumn ?? ''))
      .set('sortDirection', payload.sortDirection ?? '')

    if (payload.listaId != null) {
      params = params.set('listaId', payload.listaId.toString())
    }

    if (payload.grupoListaId != null) {
      params = params.set('grupoListaId', payload.grupoListaId.toString())
    }

    if (payload.jaContribuiu) {
      params = params.set('jaContribuiu', 'true')
    }

    Object.entries(payload.filtros).forEach(([chave, valor]) => {
      params = params.set(chave, valor?.trim() ?? '')
    })

    return this.http.get<TaxonPreenchimentoFiltroCadeiaResponse>(`${this.apiUrl}/preenchimento-filtro-cadeia`, { params })
  }

  obterPorId(id: number): Observable<TaxonDetailsDto> {
    return this.http.get<TaxonDetailsDto>(`${this.apiUrl}/${id}`)
  }

  atualizarClasseTaxonomica(avaliacaoId: number, payload: TaxonClasseTaxonomicaAtualizarDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${avaliacaoId}/classe-taxonomica`, payload);
  }

  atualizarPopulacao(avaliacaoId: number, payload: TaxonPopulacaoAtualizarDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${avaliacaoId}/populacao`, payload);
  }

  atualizarDadosEcologicos(avaliacaoId: number, payload: TaxonDadosEcologicosAtualizarDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${avaliacaoId}/dados-ecologicos`, payload);
  }

  atualizarDadosDistribuicao(avaliacaoId: number, payload: TaxonDadosDistribuicaoAtualizarDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${avaliacaoId}/dados-distribuicao`, payload);
  }

  atualizarAmeacas(avaliacaoId: number, payload: TaxonAmeacasAtualizarDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${avaliacaoId}/ameacas`, payload);
  }

  atualizarUsos(avaliacaoId: number, payload: TaxonUsosAtualizarDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${avaliacaoId}/usos`, payload);
  }

  atualizarConservacao(avaliacaoId: number, payload: TaxonConservacaoAtualizarDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${avaliacaoId}/conservacao`, payload);
  }

  atualizarPesquisa(avaliacaoId: number, payload: TaxonPesquisaAtualizarDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${avaliacaoId}/pesquisa`, payload);
  }

  atualizarMultimidia(avaliacaoId: number, payload: TaxonMultimidiaAtualizarDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${avaliacaoId}/multimidia`, payload);
  }

  atualizarValidacao(avaliacaoId: number, payload: TaxonValidacaoAtualizarDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${avaliacaoId}/validacao`, payload);
  }

  cadastrar(payload: TaxonRegistrarDto): Observable<TaxonDetailsDto> {
    return this.http.post<TaxonDetailsDto>(this.apiUrl, payload)
  }

  atualizar(id: number, payload: TaxonAtualizarDto): Observable<void> {
    return this.http.put<void>(`${this.apiUrl}/${id}`, payload)
  }

  excluir(id: number): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`)
  }
}
